<template>
  <component
    :is="to ? UiLink : 'button'"
    class="inline-flex"
    :class="[
      block ? 'w-full' : 'w-auto',
      disabled ? 'cursor-not-allowed opacity-40' : '',
      absolute ? 'absolute' : 'relative',
    ]"
    :type="type"
    :to="to"
    :disabled="disabled"
    :target="target"
    @click="clickEvent"
  >
    <span
      class="title relative flex items-center justify-center text-sm uppercase leading-none tracking-[.12em] transition"
      :class="[
        { 'w-full': block },
        { '!h-full max-h-full': fitHeight },
        { 'border-[1px]': outlined },
        { 'rounded-full': rounded },
        currentVariant,
        innerWrapperClass,
      ]"
      :style="{ minWidth }"
    >
      <slot></slot>

      <UiIcon v-if="icon" :icon="icon" class="ml-2.5 h-5 w-5" />

      <div v-if="loading" class="spinner ml-2.5 inline-block h-5 w-5"></div>
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useModalDialog } from '~/composables';
import UiLink from '~/components/ui/UiLink.vue';

interface Props {
  variant?: string;
  innerWrapperClass?: string;
  inverted?: boolean;
  size?: string;
  minWidth?: string;
  outlined?: boolean;
  flat?: boolean;
  icon?: string | null;
  loading?: boolean;
  type?: string | null;
  to?: string | null;
  target?: string | null;
  block?: boolean;
  fitHeight?: boolean;
  disabled?: boolean;
  absolute?: boolean;
  rounded?: boolean;
  modal?: string;
}
interface Emits {
  (e: 'click'): void;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  innerWrapperClass: '',
  inverted: false,
  size: 'sm',
  minWidth: undefined,
  outlined: true,
  flat: false,
  icon: null,
  loading: false,
  type: null,
  to: null,
  target: null,
  block: false,
  fitHeight: false,
  disabled: false,
  absolute: false,
  rounded: true,
  modal: undefined,
});

const emit = defineEmits<Emits>();

const variants: Record<string, any> = {
  primary: {
    base: 'bg-blue border-blue text-white hover:bg-blue-darker1 hover:border-blue-darker1 active:bg-blue-darker2 active:border-blue-darker2',
    inverted:
      'bg-white border-white text-blue hover:text-blue-darker1 hover:bg-gray-lighten5 hover:border-gray-lighten5 active:bg-gray-lighten5 active:border-gray-lighten5 active:text-blue-darker2',
  },
  secondary: {
    base: 'border-blue text-blue hover:border-blue-darker1 hover:text-blue-darker1',
    inverted: 'border-white text-white hover:border-neutral hover:text-neutral',
  },
  joinu: {
    base: 'from-[#555DE8] to-[#1EA4D6] bg-gradient-45 text-white border-transparent hover:from-blue-darker1 hover:to-blue-darker1 active:from-blue-darker2 active:to-blue-darker2',
    inverted:
      'from-cyan-500 to-blue-500 bg-gradient-45 text-white border-transparent hover:from-blue-darker1 hover:to-blue-darker1 active:from-blue-darker2 active:to-blue-darker2',
  },
  text: {
    base: 'text-blue uppercase',
    inverted: '',
  },
};

const fontSizes: Record<string, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-sm',
};

const sizes: Record<string, string> = {
  xs: 'h-8 px-4',
  sm: 'h-10 px-5',
  md: 'h-12 px-6',
  lg: 'h-16 px-6',
};

const currentVariant = computed(() => [
  variants[props.variant][props.inverted ? 'inverted' : 'base'],
  fontSizes[props.size],
  !props.flat && sizes[props.size],
]);

const clickEvent = () => {
  if (props.modal) useModalDialog(props.modal).open();
  else emit('click');
};
</script>

<style lang="sass" scoped>
.spinner
  vertical-align: text-bottom
  border: 0.15em solid currentColor
  border-right-color: transparent
  border-radius: 50%
  @apply animate-spinner-border
</style>
